// The `planning` namespace: the planning area's screens (spec 03 §4.1 to §4.4,
// §4.12, §4.13) and the BucketGrid's words. en is the source, ru is translated
// (planning.ru.ts), and fr, zh, zh-Hant and hi live in planning.<locale>.ts
// beside this file (D-430).

import type { Translated } from "../../../i18n-shape";
import { planningRuDict } from "./planning.ru";

export const planningEn = {
  nav: {
    aria: "Planning sections",
    workbench: "Workbench",
    demand: "Demand",
    forecasts: "Forecasts",
    mps: "MPS",
    orders: "Planned orders",
    schedule: "Schedule",
    capacity: "Capacity",
    parameters: "Parameters",
    calendars: "Calendars",
    setupMatrix: "Setup matrix",
    settings: "Settings",
    sectionAria: "Pages in this section",
    pool: "Demand pool",
    board: "Board",
    general: "General",
    promises: "Promises",
    whatIf: "What-if",
  },
  common: {
    loading: "Loading planning…",
    retry: "Try again",
    requestId: "Request ID",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
    edit: "Edit",
    view: "View",
    yes: "Yes",
    no: "No",
    all: "All",
    clearFilters: "Clear filters",
    filters: "Filters",
    filtersCount: "Filters ({{count}})",
    done: "Done",
    removeFilter: "Remove {{filter}}",
    previous: "Previous",
    next: "Next",
    rowsRange: "{{from}} to {{to}} of {{total}}",
    page: "Page {{page}}",
    daysCount: "{{count}} d",
    offlineWrites: "You are offline. Changes can be saved again when the network returns.",
    changedBySomeone:
      "Someone else saved first. The latest values are shown; check them and save again.",
    saveFailed: "The change could not be saved. Request ID: {{requestId}}",
    saveFailedShort: "The change could not be saved. Try again.",
  },
  access: {
    title: "Planning is not available to you",
    area: "Your role does not include planning. Ask a group admin if you need it.",
    screen:
      "This planning screen is not part of your role. Open another planning section or ask a group admin.",
  },
  facility: {
    label: "Facility",
    error: "The facilities could not be loaded.",
    noneTitle: "No facility to plan",
    noneBody: "Your access covers no active facility. Ask a group admin to add you to one.",
  },
  status: {
    active: "Active",
    archived: "Archived",
  },
  forecastStatus: {
    draft: "Draft",
    active: "Active",
    archived: "Archived",
  },
  settings: {
    title: "Planning settings",
    loading: "Loading the settings…",
    loadError: "The planning settings could not be loaded.",
    readOnly: "You can read these settings. A facility admin or group admin changes them.",
    defaults: "This facility uses the default settings. Saving creates its own.",
    saved: "Settings saved.",
    barsGroupAdmin: "Only the group admin sets the two approval thresholds.",
    mpsTitle: "Master schedule approval",
    mpsBody:
      "Below the first threshold a changed schedule is auto-approved; up to the second, a facility admin approves; above, the group admin.",
    horizonTitle: "Planning horizon",
    horizonHint: "The weeks the demand pool, the master schedule and the forecast editor cover.",
    weeks: "weeks",
    days: "days",
    minutes: "minutes",
    debounceHint:
      "After an order changes, the demand pool refreshes once this many minutes have passed, however many more changes arrive meanwhile.",
    nightlyOn: "Nightly run last claimed for {{date}}.",
    nightlyNever: "Nightly run last claimed for: never.",
    weeklyOn: "Weekly proposal last claimed for {{date}}.",
    weeklyNever: "Weekly proposal last claimed for: never.",
    fields: {
      mps_auto_approve_delta_pct: "Approve automatically up to",
      mps_facility_admin_delta_pct: "Facility admin approves up to",
      horizon_weeks: "Horizon",
      demand_refresh_debounce_minutes: "Refresh demand after a change within",
      facility_shift_pattern_id: "Facility calendar",
      mrp_release_buffer_days: "Release buffer",
      mrp_qty_tolerance_pct: "Quantity tolerance",
      schedule_horizon_days: "Schedule horizon",
      schedule_batching_window_hours: "Batch across due dates up to",
      schedule_solve_debounce_minutes: "Re-solve after a change within",
      schedule_frozen_hours: "Keep the published sequence for the next",
      overtime_cost_pct: "Price overtime at",
      schedule_auto_publish: "Publish a re-solve by itself when nothing moves too far",
    },
    calendarTitle: "Facility calendar",
    calendarHint:
      "MRP dates lead times, release buffers and due dates on this shift pattern. Patterns are kept under Calendars.",
    noCalendar: "No calendar",
    calendarSet: "This facility has a calendar. Planners and admins can see which pattern it is.",
    calendarUnset: "This facility has no calendar yet, so MRP cannot plan its dates.",
    patternsLoading: "Loading the shift patterns…",
    patternsError:
      "The shift patterns could not be loaded, so the calendar cannot be changed here.",
    archivedPattern: "{{name}} (archived)",
    mrpTitle: "MRP",
    mrpBody: "How MRP dates and sizes its planned orders.",
    workingDays: "working days",
    bufferHint: "Working days between an order's release and its start.",
    toleranceHint:
      "A firmed order whose quantity is within this share of what its demand needs gets no quantity message.",
    nightlyMrpOn: "Nightly MRP last claimed for {{date}}.",
    nightlyMrpNever: "Nightly MRP last claimed for: never.",
    scheduleTitle: "Schedule",
    scheduleBody:
      "How far ahead the finite schedule plans, how it batches and when a re-solve reaches the floor without a person.",
    hours: "hours",
    scheduleHorizonHint: "Days from today the schedule places work in, from 7 to 84.",
    frozenHint:
      "Work the published schedule starts within this many hours keeps its place on each re-solve. 0 turns this off.",
    batchingHint: "An order due this much later may be pulled forward to run without a changeover.",
    overtimeCostHint:
      "Overtime is priced at this share of the labour rate, 100 to 300. A change re-solves the schedule.",
    solveDebounceHint:
      "After a change the schedule re-solves once this many minutes have passed, however many more changes arrive.",
    autoPublishHint:
      "On: a draft with no move past tolerance and no new overload publishes itself. Off: a person publishes every draft.",
    nightlyScheduleOn: "Nightly schedule last claimed for {{date}}.",
    nightlyScheduleNever: "Nightly schedule last claimed for: never.",
    errors: {
      pct: "Enter a percentage with at most two decimals.",
      pctMax: "Enter a percentage from 0 to 100.",
      horizon: "Enter a whole number of weeks from 1 to 52.",
      debounce: "Enter a whole number of minutes from 1 to 60.",
      days: "Enter a whole number of days, 0 or more.",
      barOrder: "The automatic threshold cannot be above the facility admin threshold.",
      barsLineage: "Only the group admin can change this threshold.",
      pattern: "That shift pattern no longer exists or was archived. Choose another.",
      buffer: "Enter a whole number of working days from 0 to 10.",
      scheduleHorizon: "Enter a whole number of days from 7 to 84.",
      batching: "Enter a whole number of hours from 1 to 336.",
      frozen: "Enter a whole number of hours from 0 to 72.",
      overtime: "Enter a whole percentage from 100 to 300.",
    },
  },
  calendars: {
    title: "Calendars",
    loading: "Loading the calendars…",
    readOnly: "You can read the calendars. A facility admin or group admin changes them.",
    phoneReadOnly: "On a phone the calendars read only. Change them on a computer.",
    saved: {
      patternCreated: "Shift pattern {{code}} added.",
      patternUpdated: "Shift pattern {{code}} saved.",
      patternArchived: "Shift pattern {{code}} archived.",
      patternRestored: "Shift pattern {{code}} restored.",
      assigned: "{{pattern}} assigned to {{workCenter}}.",
      ended: "{{workCenter}}'s assignment now ends on {{date}}.",
      reopened: "{{workCenter}}'s assignment now has no end date.",
      removed: "{{workCenter}}'s future assignment removed.",
      exceptionAdded: "{{kind}} added.",
      exceptionRemoved: "{{kind}} removed.",
      facilityCalendar: "The facility calendar is now {{name}}.",
      facilityCalendarCleared: "The facility calendar was cleared.",
    },
    facility: {
      title: "Facility calendar",
      body: "MRP dates lead times, release buffers and due dates on the facility calendar: this pattern plus the holidays that close the whole facility.",
      current: "This facility plans on {{name}}.",
      none: "MRP cannot plan dates for this facility until it has a calendar.",
      pattern: "Pattern",
      noCalendar: "No calendar",
      save: "Set calendar",
      changed:
        "Someone else saved this facility's planning settings first. The calendar shown is the latest; choose again.",
      patternGone: "That shift pattern no longer exists or was archived. Choose another.",
      noPatterns: "Add an active shift pattern first, then make it the facility calendar.",
    },
    preview: {
      title: "Four weeks ahead",
      of: "Calendar of",
      facilityScope: "Whole facility",
      earlier: "Earlier weeks",
      later: "Later weeks",
      thisWeek: "This week",
      range: "{{from}} to {{to}}, times in {{zone}}",
      loading: "Loading the calendar preview…",
      loadError: "The calendar preview could not be loaded.",
      label: "Working days from {{from}}",
      working: "Working",
      closed: "Closed",
      uncalendared: "No calendar",
      hours: "{{value}} h",
      effective: "{{value}} h effective",
      atEfficiency: "at {{pct}} %",
      nextDay: "next day",
      extraShift: "Extra shift",
      closedBy: "Closed by {{names}}",
      changedBy: "Changed by {{names}}",
      uncalendaredWorkCenter:
        "This work centre has no calendar on some of these dates. MRP plans those dates at the facility calendar's hours.",
    },
    patterns: {
      title: "Shift patterns",
      add: "Add pattern",
      loading: "Loading the shift patterns…",
      loadError: "The shift patterns could not be loaded.",
      emptyTitle: "No shift patterns yet",
      emptyBody: "Add the shifts this facility works.",
      filteredEmptyTitle: "No shift pattern here",
      filteredEmptyBody: "No pattern is {{status}}. Show all patterns to see the others.",
      showAll: "Show all patterns",
      status: "Status",
      columns: {
        code: "Code",
        name: "Name",
        segments: "Shifts",
        status: "Status",
        actions: "Actions",
      },
    },
    editor: {
      newTitle: "New shift pattern",
      editTitle: "Shift pattern {{code}}",
      code: "Code",
      codeHint: "Capital letters, digits, _ and -, such as 2SHIFT_12H. It cannot change later.",
      name: "Name",
      segmentsTitle: "Shifts",
      segmentsHint:
        "One row per shift and day. A shift that ends before it starts runs into the next day and belongs to the day it starts.",
      noSegments: "No shifts yet. Add the shifts this pattern works.",
      weekday: "Day",
      start: "Start",
      end: "End",
      shiftCode: "Shift code",
      efficiency: "Efficiency, %",
      removeSegment: "Remove shift {{n}}",
      remove: "Remove",
      crosses: "Runs into the next day",
      quickTitle: "Add one shift to several days",
      days: "Days",
      addSegments: "Add shifts",
      quickHint: "Tick the days, fill the shift once and add it to every ticked day.",
      archive: "Archive",
      restore: "Restore",
      archivedNote: "This pattern is archived. Restore it to assign it again.",
      dirtyArchive: "Save or discard your changes before archiving.",
      readOnly: "You can read this pattern. A facility admin or group admin changes it.",
      errors: {
        code: "Use 1 to 64 capital letters, digits, _ or -, starting with a letter or a digit.",
        codeTaken: "Another pattern already uses this code.",
        name: "Enter a name of 1 to 120 characters.",
        changed:
          "Someone else saved this pattern first. The latest version is shown; check it and save again.",
        outOfScope:
          "This pattern is used at facilities outside your access: {{facilities}}. Ask a group admin to change it.",
        inUse:
          "This pattern is in use by {{assignments}} work-centre assignments and {{facilities}} facility calendars. End those assignments and choose another facility calendar before archiving it.",
        gone: "This pattern no longer exists. Close the editor and reload the list.",
        segments: {
          no_segments: "Add at least one shift.",
          too_many_segments: "A pattern holds at most 42 shifts.",
          weekday_out_of_range: "Choose a day from Monday to Sunday.",
          start_malformed: "Enter the start as HH:MM.",
          end_malformed: "Enter the end as HH:MM.",
          shift_code_missing: "Enter the shift code.",
          shift_code_too_long: "A shift code has at most 32 characters.",
          efficiency_out_of_range: "Efficiency is a whole number from 10 to 200.",
          zero_length: "Start and end are the same. Write a 24-hour shift as two shifts that meet.",
          overlaps: "Overlaps shift {{other}}.",
        },
      },
    },
    assignments: {
      title: "Work-centre calendars",
      add: "Assign pattern",
      loading: "Loading the work-centre calendars…",
      loadError: "The work-centre calendars could not be loaded.",
      emptyTitle: "No work centre has a calendar yet",
      emptyBody:
        "Assign a shift pattern to each work centre. Until then MRP plans a work centre at the facility calendar's hours.",
      filteredEmptyBody: "{{workCenter}} has no calendar yet. Assign a pattern to it.",
      filter: "Work centre",
      allWorkCenters: "All work centres",
      columns: {
        workCenter: "Work centre",
        pattern: "Pattern",
        from: "From",
        to: "To",
        state: "State",
        warning: "Shift check",
        actions: "Actions",
      },
      openEnded: "No end",
      inEffect: "In effect",
      future: "Starts later",
      ended: "Ended",
      shiftsOk: "Matches production shifts",
      missingCodes: "No production shift {{codes}}",
      missingCodesHint:
        "Production has no active shift definition for these codes at this facility. Planning still uses the pattern; production attributes work to its own shifts.",
      end: "Change end",
      remove: "Remove",
      dialog: {
        title: "Assign a shift pattern",
        workCenter: "Work centre",
        pattern: "Pattern",
        validFrom: "First day",
        validTo: "Last day",
        validToHint: "Leave empty for no end.",
        save: "Assign",
        choose: "Choose…",
      },
      endDialog: {
        title: "End the assignment of {{workCenter}}",
        validTo: "Last day",
        openEnded: "No end date",
        save: "Save",
      },
      errors: {
        required: "Choose a work centre, a pattern and a first day.",
        reversed: "The last day cannot be before the first.",
        overlap: "Overlaps the assignment from {{from}} to {{to}}.",
        overlapOpen: "Overlaps the assignment from {{from}} with no end.",
        overlapUnknown: "Overlaps another assignment of this work centre.",
        workCenterGone: "That work centre is not active at this facility any more.",
        patternGone: "That shift pattern no longer exists or was archived.",
        patternArchived:
          "This assignment's shift pattern is archived, so it cannot run past today. Restore the pattern or assign another.",
        inEffect: "This assignment has already started. Change its end instead.",
        gone: "This assignment no longer exists. The list has been reloaded.",
      },
    },
    exceptions: {
      title: "Holidays, downtime and extra shifts",
      add: "Add exception",
      loading: "Loading the exceptions…",
      loadError: "The exceptions could not be loaded.",
      emptyTitle: "No exception in these dates",
      emptyBody:
        "Add a holiday, a maintenance stop, a breakdown, overtime or an extra shift when the hours change.",
      filteredEmptyBody:
        "Nothing matches {{filters}} in these dates. Clear the filters to see every exception.",
      from: "From",
      to: "To",
      kind: "Kind",
      allKinds: "All kinds",
      scope: "Applies to",
      wholeFacility: "Whole facility",
      allScopes: "Facility and every work centre",
      columns: {
        kind: "Kind",
        scope: "Applies to",
        starts: "Starts",
        ends: "Ends",
        factor: "Capacity",
        note: "Note",
        actions: "Actions",
      },
      closed: "Closed",
      factor: "× {{factor}}",
      allDayRange: "Whole days",
      remove: "Remove",
      kinds: {
        holiday: "Holiday",
        planned_maintenance: "Planned maintenance",
        breakdown: "Breakdown",
        extra_shift: "Extra shift",
        overtime: "Overtime",
      },
      window: {
        malformed: "Enter both dates.",
        reversed: "The last date is before the first.",
        too_wide: "Choose at most 731 days.",
      },
      dialog: {
        title: "Add an exception",
        scope: "Applies to",
        kind: "Kind",
        allDay: "Whole days",
        startDate: "First day",
        startTime: "Starts at",
        endDate: "Last day",
        endTime: "Ends at",
        factor: "Capacity factor",
        factorHint:
          "0 closes. 1 is the usual capacity. Above 1, up to 3, is allowed for overtime and extra shifts.",
        note: "Note",
        save: "Add",
      },
      errors: {
        starts_malformed: "Enter when it starts.",
        ends_malformed: "Enter when it ends.",
        ends_not_after_starts: "It must end after it starts.",
        factor_malformed: "Enter a number with at most two decimals.",
        factor_out_of_range: "A capacity factor is at most 3.",
        factor_above_one_for_kind: "Only overtime and extra shifts may raise capacity above 1.",
        note_too_long: "A note has at most 500 characters.",
        kind_unknown: "Choose a kind.",
        starts_at_malformed: "Enter when it starts.",
        ends_at_malformed: "Enter when it ends.",
        workCenterGone: "That work centre is not active at this facility any more.",
        gone: "This exception no longer exists. The list has been reloaded.",
      },
    },
  },
  workbench: {
    title: "Planning workbench",
    loading: "Loading the refresh history…",
    loadError: "The workbench could not be loaded.",
    emptyTitle: "No demand refresh yet",
    emptyBody: "Run one or wait for the 02:00 run.",
    last: "Last refresh {{time}}",
    rerun: "Run again",
    pending: "Refresh queued by {{count}} changes, runs at {{time}}.",
    pending_one: "Refresh queued by {{count}} change, runs at {{time}}.",
    retrying: "Failed attempts so far: {{count}}. Next try at {{time}}.",
    failures:
      "Failed refreshes in a row: {{count}}. The pool shown is the last one that succeeded.",
    historyTitle: "Refresh history",
    duration: "{{minutes}} min {{seconds}} s",
    durationSeconds: "{{seconds}} s",
    durationUnderSecond: "Under 1 s",
    deferredTitle: "Lines waiting on credit",
    deferredBody:
      "{{count}} order lines wait outside the pool while their order is on credit block. They return at the next refresh after release.",
    deferredBody_one:
      "{{count}} order line waits outside the pool while its order is on credit block. It returns at the next refresh after release.",
    skippedTitle: "Lines left out",
    skippedBody:
      "{{count}} lines are left out because their unit cannot be converted to the product's base unit. Correct the unit on the order or the product.",
    skippedBody_one:
      "{{count}} line is left out because its unit cannot be converted to the product's base unit. Correct the unit on the order or the product.",
    shipmentLine: "A shipment line read for average demand",
    uomNotConvertible: "unit does not convert",
    columns: {
      started: "Started",
      trigger: "Started by",
      status: "Status",
      duration: "Took",
      orderLines: "Order lines",
      orderLine: "Order line",
      why: "Why",
      entries: "Entries",
      deferred: "Credit deferred",
      skipped: "Skipped",
      error: "Error",
    },
    tiles: {
      openDemand: "Open demand",
      orderLines: "Order lines",
      deferred: "Waiting on credit",
      deferredSub: "order lines left out",
      skipped: "Left out",
      skippedSub: "unit does not convert",
      noRefresh: "No refresh has succeeded yet",
      asOf: "From the latest successful refresh",
      partial: "{{count}} rows still loading their product",
      mps: "Master schedule",
      mpsNone: "No plan yet",
      mpsError: "The plan could not be read",
      mpsProposed: "proposed {{time}}",
    },
  },
  demand: {
    title: "Demand pool",
    loading: "Loading the demand pool…",
    loadError: "The demand pool could not be loaded.",
    refresh: "Refresh demand",
    refreshing: "Refreshing…",
    refreshDone: "Demand refreshed at {{time}}.",
    refreshFailed: "The refresh failed: {{error}}. The previous pool stays in force.",
    refreshRequestFailed: "The refresh could not be started. Request ID: {{requestId}}",
    noRefreshTitle: "No demand refresh has run here yet",
    noRefreshBody: "Run one or wait for the next run.",
    emptyTitle: "Demand pool is empty",
    emptyBody: "Confirm sales orders or activate a forecast.",
    filteredEmptyTitle: "No demand matches these filters",
    filteredEmptyBody: "Filters in use: {{filters}}.",
    replaced:
      "A newer refresh replaced the pool you were reading, so the latest pool is shown now.",
    shown: "Pool of the refresh finished {{time}}",
    transferOrder: "Transfer {{number}} to {{facility}}",
    transferTo: "Transfer to {{facility}}",
    salesOrder: "Sales order",
    lineNo: "line {{line}}",
    subtotalsTitle: "Net demand by product and week",
    entriesTitle: "Demand entries",
    drillHint: "Open a forecast row to see which orders consumed it.",
    views: {
      label: "Demand pool view",
      entries: "Entries",
      totals: "Totals by week",
    },
    filterChip: "{{filter}}: {{value}}",
    filters: {
      product: "Product",
      source: "Source",
      basis: "Date",
      from: "From week",
      to: "To week",
      plannerCode: "Planner code",
    },
    columns: {
      product: "Product",
      source: "Source",
      basis: "Date",
      bucket: "Week",
      due: "Due",
      original: "Quantity",
      consumed: "Consumed",
      net: "Net",
      total: "Total",
    },
    source: {
      sales_order: "Order",
      forecast: "Forecast",
      safety_stock: "Safety stock",
      transfer: "Transfer",
    },
    basis: {
      line_promise: "Promise date",
      undated: "No promise date, planned for this week",
      past_due: "Past due",
    },
    trigger: {
      cron: "Nightly",
      manual: "By hand",
      event: "After changes",
      mrp: "By MRP",
    },
    status: {
      running: "Running",
      succeeded: "Done",
      failed: "Failed",
    },
    consumption: {
      title: "{{product}}, {{week}}: consumed by orders",
      loading: "Loading the consumption…",
      loadError: "The consumption could not be loaded.",
      none: "This forecast has no line in this week in the refresh shown.",
      noOrders: "No order consumed this week's forecast.",
      summary: "Forecast {{original}}, consumed {{consumed}}, net {{net}}.",
    },
  },
  parameters: {
    title: "Planning parameters",
    loading: "Loading the planning parameters…",
    loadError: "The planning parameters could not be loaded.",
    readOnly: "You can read the parameters. A planner, facility admin or group admin changes them.",
    emptyTitle: "No planning parameters yet",
    emptyBody: "Add the products this facility plans.",
    filteredEmptyTitle: "No products match these filters",
    filteredEmptyBody: "Filters in use: {{filters}}.",
    addProduct: "Add product",
    sectionTitle: "Parameters",
    drawerLabel: "Planning for {{product}}",
    save: "Save parameters",
    archive: "Archive",
    restore: "Restore",
    archiveConfirmTitle: "Archive these parameters",
    archiveConfirm:
      "Archive this product's parameters? It stops being planned at this facility until it is restored.",
    archiveConfirmPolicy:
      "Archive this product's parameters? Its safety stock ({{policy}}) is archived with them, and it stops being planned at this facility until it is restored.",
    archivedRow: "These parameters are archived. Restore them to plan this product again.",
    savedNotice: "Parameters saved.",
    archivedNotice: "Parameters archived.",
    restoredNotice: "Parameters restored.",
    chooseSource: "Choose a facility",
    otherFacility: "another facility",
    transferFrom: "Transfer from {{facility}}",
    mpsYes: "In the master schedule",
    mpsNo: "Not in the master schedule",
    columns: {
      product: "Product",
      replenishment: "Replenishment",
      mps: "Master schedule",
      leadTime: "Lead time",
      lotSizing: "Lot sizing",
      fence: "Frozen fence",
      tolerance: "Reschedule tolerance",
      plannerCode: "Planner code",
      safetyStock: "Safety stock",
      status: "Status",
      actions: "Actions",
    },
    filters: {
      status: "Status",
      replenishment: "Replenishment",
      mps: "Master schedule",
    },
    fields: {
      replenishment: "Replenishment",
      source: "Source facility",
      mps: "In the master schedule",
      leadTime: "Lead time, days",
      lotSizing: "Lot sizing",
      minLot: "Minimum lot",
      multiple: "Lot multiple",
      fence: "Frozen fence, days",
      tolerance: "Reschedule tolerance, days",
      plannerCode: "Planner code",
    },
    replenishment: {
      make: "Make",
      buy: "Buy",
      transfer: "Transfer",
    },
    lot: {
      lot_for_lot: "Lot for lot",
      min_multiple: "Minimum and multiple",
      minMultipleShort: "Min {{min}}, multiple {{multiple}}",
    },
    errors: {
      wholeDays: "Enter a whole number of days, 0 or more.",
      positiveQty: "Enter a quantity above zero with at most three decimals.",
      plannerCode: "A planner code has at most 64 characters.",
    },
    reasons: {
      METHOD_FIELDS_MISMATCH:
        "Minimum and multiple needs a minimum lot. Lot for lot takes neither quantity.",
      TRANSFER_SOURCE_REQUIRED: "A transfer needs the facility it comes from.",
      SOURCE_NOT_ALLOWED: "Only a transfer names a source facility.",
      SOURCE_FACILITY_INVALID: "Choose another active facility of the group as the source.",
      PRODUCT_ARCHIVED: "The product is archived in the catalogue.",
      PRODUCT_NOT_FOUND: "The product no longer exists in the catalogue.",
    },
    problems: {
      PRODUCT_ARCHIVED: "This product is archived in the catalogue, so it cannot be planned.",
      PRODUCT_NOT_FOUND: "This product no longer exists in the catalogue.",
    },
  },
  safety: {
    title: "Safety stock",
    current: "Current policy: {{policy}}",
    none: "None",
    staticShort: "Keep {{qty}}",
    daysShort: "Cover: {{count}} d",
    needsParameters: "Save active parameters first. Safety stock belongs to a planned product.",
    method: "Method",
    methods: {
      static_qty: "Fixed quantity",
      days_of_cover: "Days of cover",
    },
    staticQty: "Quantity to keep",
    daysOfCover: "Days of cover",
    validFrom: "Valid from",
    validFromHint: "Leave empty to start today.",
    validTo: "Valid to",
    save: "Save safety stock",
    remove: "Remove safety stock",
    savedNotice: "Safety stock saved.",
    removedNotice: "Safety stock removed.",
    errors: {
      qty: "Enter a quantity, 0 or more, with at most three decimals.",
      days: "Enter a whole number of days, 1 or more.",
      invalid: "Check the values and save again.",
    },
    reasons: {
      METHOD_FIELDS_MISMATCH: "This method needs its own value and no other.",
      VALID_RANGE_INVALID: "The end date cannot be before the start date.",
      NO_PLANNING_PARAMETERS: "Save active parameters for this product first.",
    },
  },
  bulk: {
    selectAll: "Select every row on this page",
    selectRow: "Select {{product}}",
    count: "Selected: {{count}}",
    clear: "Clear selection",
    field: "Field",
    value: "Value",
    apply: "Apply to selected ({{count}})",
    applying: "Applying…",
    saved: "Saved: {{count}}",
    changed: "Changed by someone else: {{count}}",
    failed: "Not saved: {{count}}",
    refused: "Refused, {{reason}}: {{count}}",
    row: {
      changed: "someone else saved this row first",
      failed: "it could not be saved",
    },
  },
  forecasts: {
    title: "Forecasts",
    loading: "Loading the forecasts…",
    loadError: "The forecasts could not be loaded.",
    emptyTitle: "No forecasts yet",
    emptyBody: "Create one for the demand you expect before it is ordered.",
    filteredEmptyTitle: "No forecasts with this status",
    filteredEmptyBody: "Status shown: {{status}}.",
    filterStatus: "Status",
    new: "New forecast",
    create: "Create",
    windowDays: "± {{count}} d",
    windowHint: "Orders this many days either side of a week consume its forecast. 0 to 91.",
    columns: {
      name: "Name",
      status: "Status",
      mode: "Mode",
      window: "Consumption window",
      lines: "Lines",
      updated: "Updated",
    },
    fields: {
      name: "Name",
      mode: "Mode",
      window: "Consumption window, days",
    },
    modes: {
      consume: "Consumed by orders",
      additive: "Added to orders",
    },
    modeHints: {
      consume: "Orders use up the forecast, so demand is the larger of the two.",
      additive: "Forecast and orders add up, for demand no order will cover.",
    },
    errors: {
      name: "Give the forecast a name of up to 120 characters.",
      nameTaken: "Another forecast at this facility already has this name.",
      window: "Enter a whole number of days from 0 to 91.",
    },
  },
  editor: {
    title: "Forecast",
    loading: "Loading the forecast…",
    loadError: "The forecast could not be loaded.",
    notFound: "This forecast does not exist, or it belongs to a facility outside your access.",
    empty: "No lines in these weeks. Add a product to start.",
    outside: "Lines outside these weeks: {{count}}",
    archivedBanner: "This forecast is archived. It can no longer be edited or made active.",
    phoneReadOnly: "The forecast reads only on a phone. Open it on a wider screen to edit.",
    saveDetails: "Save details",
    activate: "Activate",
    archive: "Archive",
    archiveConfirm:
      "Archive this forecast? An archived forecast can never be edited or made active again.",
    detailsSavedNotice: "Details saved.",
    activatedNotice: "The forecast is active.",
    archivedNotice: "The forecast is archived.",
    transitionInvalid: "The forecast cannot move to that status from where it is.",
    headerInvalid: "Check the name and the window, then save again.",
    earlier: "Earlier",
    later: "Later",
    windowRange: "{{from}} to {{to}}",
    saveBeforeMoving: "Save or discard your changes to move to other weeks.",
    addProduct: "Add a product",
    pasteHint:
      "Paste a block from a spreadsheet. A first row of dates sets the weeks; a first column of SKUs sets the products.",
    note: "Note for {{product}}, {{week}}",
    noteNeedsQty: "Enter a quantity in this week before adding a note.",
    save: "Save changes, products: {{count}}",
    discard: "Discard changes",
    changedUnderneath: "Someone else changed this week's saved value. Check it and save again.",
    leaveTitle: "Unsaved changes",
    leaveBody: "Changes to products not saved: {{count}}. Leave and lose them?",
    leave: "Leave without saving",
    stay: "Stay",
    report: {
      saved: "Saved, products: {{count}} ({{products}}).",
      refused: "Not saved, products: {{count}} ({{products}}). The marked cells say why.",
      changed:
        "Someone else saved this forecast first. The latest lines are shown with your changes kept.",
      archived: "This forecast was archived, so nothing more was saved.",
      weekRolled:
        "A new week has started, so last week is read-only now. Your other changes are kept, so save again.",
      pastDropped: "A new week has started, so your changes to last week were not saved.",
    },
    rowCodes: {
      BUCKET_NOT_MONDAY: "The week must start on a Monday",
      BUCKET_OUTSIDE_WINDOW: "This week is outside the weeks shown",
      DUPLICATE_LINE: "This product and week appear twice",
      PRODUCT_NOT_FOUND: "This product no longer exists",
      PRODUCT_ARCHIVED: "This product is archived in the catalogue",
      PRODUCT_OUTSIDE_SCOPE: "This product was not part of the save",
      QTY_NEGATIVE: "The quantity cannot be negative",
      QTY_PRECISION: "At most three decimals",
      QTY_TOO_LARGE: "The quantity is too large",
      NOTE_TOO_LONG: "The note is longer than 500 characters",
    },
  },
  paste: {
    summary: "Values pasted: {{count}}. Save to keep them.",
    snapped: "{{text}} is not a Monday, so its values went to the week of {{week}}.",
    problems: {
      not_number: "Line {{line}}, column {{column}}: {{text}} is not a number.",
      precision: "Line {{line}}, column {{column}}: {{text}} has more than three decimals.",
      week_outside:
        "Line {{line}}, column {{column}}: this week is outside the weeks you can edit here.",
      unknown_product: "Line {{line}}: {{text}} is not a product the grid knows.",
      lookup_failed: "Line {{line}}: {{text}} could not be looked up. Paste again to retry.",
      over_key_limit:
        "Line {{line}}: {{text}} is past the 200 products one paste can look up. Paste it separately.",
    },
  },
  picker: {
    placeholder: "SKU or name",
    results: "Matching products",
    searching: "Searching…",
    error: "The products could not be searched.",
    none: "No product matches.",
  },
  mps: {
    title: "Master production schedule",
    loading: "Loading the master schedule…",
    loadError: "The master schedule could not be loaded.",
    status: {
      proposed: "Proposed",
      adjusting: "Being adjusted",
      pending_approval: "Waiting for approval",
      approved: "Approved",
      superseded: "Superseded",
    },
    autoApproved: "Approved automatically on submit",
    proposedAt: "Proposed {{time}}",
    proposeNow: "Propose now",
    proposing: "Proposing…",
    submit: "Submit for approval",
    submitting: "Submitting…",
    approve: "Approve",
    reject: "Reject",
    plans: "Plans",
    plansEmpty: "No plans yet at this facility.",
    previousPlan: "the approved plan",
    totals: "Total in this plan:",
    diff: "Show the change against the approved plan",
    diffLoading: "Loading the approved plan to compare…",
    editHint:
      "Double-click a cell or press Enter to change it. Every change needs a reason. Cells with a lock are inside the frozen fence.",
    noPlannerCode: "No planner code",
    plannerCode: "Planner {{code}}",
    loadMore: "Load more products",
    loadingMore: "Loading products…",
    receiptsHint:
      "Receipts count released and in-progress work orders, transfers in transit and firmed planned orders, as read at {{time}}. Propose now reads them again.",
    transferNote:
      "{{facilities}} sells products made here; MRP turns its orders into transfer demand on this schedule.",
    demandChanged: "Demand changed after this plan was proposed.",
    demandChangedCells:
      "Demand changed since this plan was proposed: {{count}} cells differ from the demand it was built on.",
    demandChangedCells_one:
      "Demand changed since this plan was proposed: {{count}} cell differs from the demand it was built on.",
    returned: "Returned by {{who}} on {{date}}: {{note}}",
    routed:
      "Routed to the group admin. This plan changes the schedule by {{pct}} %, which is above what a facility admin may approve.",
    earlier: "You are looking at an earlier plan. It is read only.",
    earlierSuperseded: "This plan was superseded by a newer one. It is read only.",
    backToCurrent: "Open the current plan",
    openReplacement: "Open the plan that replaced it",
    reload: "Reload",
    conflict: {
      changed: "This plan changed. Reload.",
      superseded: "Plan superseded. Reload.",
    },
    threshold: {
      first: "This is the facility's first master schedule, so the group admin approves it.",
      delta: "Change against the approved plan: {{pct}} % in {{unit}}.",
      deltaNoUnit: "Change against the approved plan: {{pct}} %.",
      aboveBar:
        "That is above the facility admin's limit of {{bar}} %, so the group admin approves it.",
      withinBar: "A facility admin may approve it (limit {{bar}} %).",
      products: "Largest changes among the products shown:",
    },
    sub: {
      demand: "Demand",
      receipts: "Scheduled receipts",
      projected: "Projected on hand at week start",
      proposed: "System proposal",
    },
    receipt: {
      work_order: "Work order {{ref}}: {{qty}}",
      transfer: "Transfer {{ref}}: {{qty}}",
      planned_order: "Firmed planned order {{ref}}: {{qty}}",
      due: "due {{date}}",
      noDue: "no due date, counted in the first week",
      overdue: "was due {{date}}, counted in the first week",
      released:
        "{{qty}} of this week's frozen quantity is already in firmed planned orders or work orders released against the approved plan, so it is counted once",
      heldBy: {
        planned_order: "held by firmed planned order {{ref}}: {{qty}}, due {{date}}",
        work_order: "held by work order {{ref}}: {{qty}}, due {{date}}",
      },
    },
    note: {
      overridden: "Changed by {{who}} on {{time}}. Reason: {{reason}}",
      droppedChanged:
        "Demand changed, override dropped. It was {{qty}} in {{plan}}. Reason: {{reason}}",
      droppedFrozen:
        "Now inside the frozen fence, override dropped. It was {{qty}} in {{plan}}. Reason: {{reason}}",
    },
    columns: {
      plan: "Plan",
      current: "current",
      status: "Status",
      proposed: "Proposed",
      delta: "Change",
    },
    approveTitle: "Approve the plan",
    approveBody: "Approve {{plan}}. It replaces the approved plan in force at this facility.",
    rejectTitle: "Reject the plan",
    rejectBody: "Return {{plan}} to the planner. Say what needs to change.",
    noteOptional: "Note (optional)",
    noteRequired: "Note",
    outcome: {
      proposed: "{{plan}} was proposed.",
      submitted: "{{plan}} was submitted and waits for approval.",
      autoApproved: "{{plan}} was approved automatically on submit.",
      approved: "{{plan}} was approved.",
      rejected: "{{plan}} was returned to the planner.",
    },
    errors: {
      pending: "A submitted plan waits for approval. Approve or reject it first.",
      refreshMissing:
        "There is no demand refresh for this week yet. Refresh demand, then propose again.",
      noRelevant:
        "No product is master-scheduled at this facility. Mark products MPS-relevant in Planning parameters.",
      proposeFailed: "The plan could not be proposed. Request ID: {{requestId}}",
      submitFailed: "The plan could not be submitted. Request ID: {{requestId}}",
      lineChanged: "Someone changed this cell first. The latest value is shown.",
      frozen: "This week is inside the frozen fence and cannot change.",
      reasonRequired: "Give a reason for the change.",
      invalid: "Enter a quantity of 0 or more with at most three decimals.",
    },
    empty: {
      noRefreshTitle: "No master schedule yet",
      noRefreshBody: "MPS proposals appear automatically after the first demand refresh.",
      noProductsTitle: "No product is master-scheduled here",
      noProductsBody:
        "No product is master-scheduled at this facility. Mark products MPS-relevant in Planning parameters.",
      toParameters: "Open Planning parameters",
      notProposedTitle: "The first proposal is on its way",
      notProposedBody:
        "A plan is proposed automatically after the next demand refresh. Propose now makes one at once.",
      noLinesTitle: "This plan has no products",
      noLinesBody: "No product was master-scheduled when this plan was proposed.",
    },
  },
  orders: {
    title: "Planned orders",
    loading: "Loading the planned orders…",
    loadError: "The planned orders could not be loaded.",
    emptyTitle: "No planned orders",
    emptyBody: "Approve an MPS plan or run MRP.",
    filteredEmptyTitle: "No planned order matches these filters",
    filteredEmptyBody: "Filtered by {{filters}}. Clear the filters to see every order.",
    noMessagesTitle: "No action messages",
    noMessagesBody:
      "No firmed order at this facility needs a change. MRP raises a message when one does.",
    purchaseOnly:
      "You see purchase orders only. Manufacture and transfer orders are planned by the plant.",
    purchaseOnlyShort: "purchase orders only",
    tabs: {
      label: "Planned order lists",
      all: "All",
      messages: "Action messages",
    },
    columns: {
      poNo: "Number",
      kind: "Kind",
      product: "Product",
      qty: "Quantity",
      start: "Start",
      due: "Due",
      status: "Status",
      flags: "Flags",
      suggestion: "Suggestion",
      document: "Document",
    },
    // The document a converted order became (§3.4, D-389 rulings 4 to 6).
    document: {
      work_order: "Draft work order",
      transfer_order: "Draft transfer",
      purchase_proposal: "Purchase proposal",
    },
    kind: {
      manufacture: "Make",
      purchase: "Buy",
      transfer: "Transfer",
    },
    status: {
      proposed: "Proposed",
      firmed: "Firmed",
      converted: "Converted",
      cancelled: "Cancelled",
    },
    message: {
      reschedule_in: "Bring in",
      reschedule_out: "Push out",
      increase_qty: "Increase",
      decrease_qty: "Decrease",
      cancel: "Cancel",
      expedite: "Expedite",
    },
    suggest: {
      reschedule_in: "Move the due date in to {{value}}",
      reschedule_out: "Move the due date out to {{value}}",
      increase_qty: "Raise the quantity to {{value}}",
      decrease_qty: "Lower the quantity to {{value}}",
      cancel: "Cancel the order, nothing needs it",
      expedite: "Release it today, its release date has passed",
    },
    reason: {
      no_need_in_horizon: "No need in the horizon is left for this order.",
      release_in_past: "The release date is already behind today.",
      need_earlier: "The need it covers is now earlier.",
      need_later: "The need it covers is now later.",
      share_below_qty: "Less of the order is needed than it holds.",
      need_uncovered_inside_fence: "A need inside the frozen fence is not covered.",
    },
    cancelledKind: {
      regenerated_away: "Replaced by a later run",
      manual: "Cancelled by a person",
      action_message_cancel: "Cancelled from its action message",
    },
    filters: {
      kind: "Kind",
      status: "Status",
      message: "Action message",
      from: "Due from",
      to: "Due to",
      product: "Product",
      planner: "Planner code",
      run: "MRP run",
      line: "Sales order line",
      entry: "Demand entry",
    },
    filterTag: {
      run: "Run {{run}}",
      product: "One product",
      line: "Driven by one order line",
      entry: "Driven by one demand entry",
    },
    removeFilter: "Remove the filter {{filter}}",
    messageAny: "Any message",
    messageNone: "No message",
    late: "Late",
    exceedsMps: "Exceeds MPS",
    stalePin: "Stale BOM or routing",
    firm: "Firm",
    convert: "Convert",
    cancelOrder: "Cancel order",
    cancelOrders: "Cancel",
    accept: "Accept",
    dismiss: "Dismiss",
    cancelReason: "Why is it cancelled?",
    dismissReason: "Why is it dismissed?",
    selectPage: "Select the orders on this page",
    selectRow: "Select {{number}}",
    selectedCount: "{{count}} selected",
    selectedCount_one: "{{count}} selected",
    clearSelection: "Clear",
    selectMatching: "Select every matching order (at most {{max}})",
    selecting: "Selecting…",
    overMax: "More orders match than one action takes. The first {{max}} are selected.",
    bulk: {
      firmTitle: "Firm planned orders",
      firmBody:
        "Firm {{count}} planned orders: {{kinds}}. A firmed order keeps its quantity and dates, and MRP sends a message instead of changing it.",
      firmBody_one:
        "Firm {{count}} planned order: {{kinds}}. A firmed order keeps its quantity and dates, and MRP sends a message instead of changing it.",
      firmConfirm: "Firm",
      cancelTitle: "Cancel firmed orders",
      cancelBody:
        "Cancel the firmed ones of {{count}} selected orders: {{kinds}}. Each is cancelled on its own, with the one reason.",
      cancelBody_one: "Cancel {{count}} selected order: {{kinds}}.",
      cancelConfirm: "Cancel orders",
      acceptTitle: "Accept action messages",
      acceptBody:
        "Accept the push-out message of {{count}} orders: {{kinds}}. Each moves to the date its message names.",
      acceptBody_one:
        "Accept the push-out message of {{count}} order: {{kinds}}. It moves to the date its message names.",
      acceptConfirm: "Accept",
      convertTitle: "Convert firmed orders",
      convertBody:
        "Convert the firmed ones of {{count}} selected orders ({{kinds}}) into draft work orders and transfers. A refusal stops only that order.",
      convertBody_one:
        "Convert {{count}} selected order: {{kinds}}. A make order becomes a draft work order and a transfer order a draft transfer.",
      convertConfirm: "Convert",
      convertNotFirmed:
        "{{count}} of them are not firmed, so they are not sent. Firm an order before you convert it.",
      convertNotFirmed_one:
        "{{count}} of them is not firmed, so it is not sent. Firm an order before you convert it.",
      convertBuy:
        "{{count}} of them are buy orders, which can't be converted here, so each is refused and stays firmed.",
      convertBuy_one:
        "{{count}} of them is a buy order, which can't be converted here, so it is refused and stays firmed.",
      notFirmed:
        "{{count}} of them are not firmed, so they are not sent. A proposed order is replaced by the next run.",
      notFirmed_one:
        "{{count}} of them is not firmed, so it is not sent. A proposed order is replaced by the next run.",
      kindCount: {
        manufacture: "{{count}} make",
        purchase: "{{count}} buy",
        transfer: "{{count}} transfer",
      },
    },
    result: {
      firmTitle: "Firm results",
      cancelTitle: "Cancel results",
      acceptTitle: "Accept results",
      convertTitle: "Conversion results",
      summary: "Done: {{done}}. Refused or not sent: {{refused}}.",
      firmed: "Firmed",
      accepted: "Accepted",
      cancelled: "Cancelled",
      converted: "Converted",
      alreadyConverted: "Converted before",
      notSent: "Not firmed, so not sent",
    },
    refusal: {
      PLANNED_ORDER_NOT_PROPOSED: "No longer proposed",
      PLANNED_ORDER_NOT_FIRMED: "Not firmed",
      PLANNED_ORDER_NOT_FOUND: "This order no longer exists",
      NO_ACTION_MESSAGE: "Its message was already cleared",
      ACTION_MESSAGE_CHANGED: "Its message changed since you read it",
      ACTION_MESSAGE_NOT_BULK_ACCEPTABLE: "This message is accepted one order at a time",
      PERMISSION_DENIED: "You may not change this order",
      PURCHASE_CONVERSION_UNAVAILABLE: "A buy order can't be converted into a purchase order here",
      PLANNED_ORDER_NO_ROUTING:
        "No routing is in force for this product. Add one in the catalogue and convert again.",
      PLANNED_ORDER_PIN_INVALID: "Its pinned BOM or routing can no longer be used.",
      ORDER_LINE_NOT_FOUND: "The sales order line it is planned for no longer exists",
      ORDER_NOT_CONFIRMED: "The sales order it is planned for is not confirmed",
      ORDER_TERMINAL: "The sales order it is planned for is shipped, closed or cancelled",
      ORDER_LINE_TERMINAL: "The sales order line it is planned for is ready, shipped or cancelled",
      ORDER_LINE_PRODUCT_MISMATCH: "The sales order line it is planned for is for another product",
      TRANSFER_SOURCE_INACTIVE: "The source facility is archived",
      FACILITY_NOT_FOUND: "A facility of this transfer no longer exists",
      PRODUCT_NOT_FOUND: "Its product no longer exists",
      other: "Refused ({{code}})",
    },
    pin: {
      bom_archived:
        "The pinned BOM is archived. Cancel the order and the next run proposes it on the BOM in force.",
      routing_archived:
        "The pinned routing is archived. Cancel the order and the next run proposes it on the routing in force.",
      routing_without_steps: "The pinned routing has no steps. Add its steps, or cancel the order.",
    },
    noRoutingOn: "It was looked for on {{date}}.",
    ref: {
      sales_order_line: "Sales order line",
      forecast_line: "Forecast",
      safety_stock_policy: "Safety stock",
      transfer_planned_order: "Transfer to another facility",
      mps_line: "MPS cell",
      planned_order: "Parent planned order",
      work_order: "Work order",
      excess: "Lot excess, no need yet",
      parentOrder: "Parent order {{number}}",
      transferOrder: "Transfer {{number}} to {{facility}}",
      transferOrderShort: "Transfer {{number}}",
      mpsCell: "MPS cell of {{number}}",
      workOrder: "Work order {{number}}",
    },
    drawer: {
      label: "Planned order",
      loadingTitle: "Planned order",
      loading: "Loading the planned order…",
      loadError: "The planned order could not be loaded.",
      gone: "This planned order was regenerated away. Its number belongs to no order now.",
      release: "Release",
      source: "Source facility",
      firmed: "Firmed",
      cancelled: "Cancelled",
      converted: "Converted to",
      byAt: "{{who}}, {{time}}",
      stalePinProposed:
        "The BOM or routing changed after this order was pinned. The next run explodes it again.",
      stalePinFirmed:
        "The BOM or routing changed after pinning. A firmed order keeps its pins; cancel it so MRP proposes the new version.",
      exceedsMps: "This order plans more than the approved MPS cell holds.",
      demand: "Demand",
      noDemand: "Nothing drives this order; it holds lot excess only.",
      neededBy: "needed by {{date}}",
      chainUp: "Up the chain",
      walkHidden: "{{count}} more links are not shown.",
      walkHidden_one: "{{count}} more link is not shown.",
      qtyMath: "Quantity math",
      dateMath: "Date math",
      drives: "Drives",
      drivesNone: "No other planned order depends on this one.",
      message: "Action message",
      deltaDays: "({{count}} days)",
      deltaDays_one: "({{count}} day)",
      acceptConfirm: "Accept: {{message}}?",
      messageNow: "It now says: {{message}}.",
      messageGone: "It no longer carries a message.",
      lastDismissed: "Last dismissed: {{message}}. Reason: {{reason}}",
      firmedDone: "{{number}} is firmed.",
      cancelledDone: "{{number}} is cancelled.",
      convertedDone: "{{number}} is converted.",
      convertBuy: "A buy order is firmed and planned like any other, but can't be converted here.",
      acceptedDone: "The message on {{number}} is accepted.",
      dismissedDone: "The message on {{number}} is dismissed.",
    },
    waterfall: {
      step: "Step",
      before: "Before",
      term: "Term",
      after: "After",
      scrap_batch: "Per batch of",
      scrap_component: "Component scrap",
      scrap_assembly: "Assembly scrap",
      scrap_operation: "Operation {{step}} scrap",
      item_needs: "This need comes to",
      gross: "Gross requirement",
      remnants_matched: "Remnants",
      remnant: "Remnant {{no}}",
      remnant_length: "Remnant {{no}}, {{length}} m",
      on_hand: "On hand",
      offcuts_matched: "of which off-cuts held for pooled lines",
      reserved: "Reserved for other orders, not counted",
      in_transit: "In transit",
      open_supply: "Open work and purchase orders",
      firmed: "Firmed planned orders",
      co_by_products: "Co- and by-products",
      lot_excess_carried: "Lot excess carried forward",
      net: "Net requirement",
      lot_for_lot: "Lot sizing: lot for lot",
      min_multiple: "Lot sizing: at least {{min}}, in steps of {{multiple}}",
      mps_cell: "Approved MPS cell",
      mps_committed: "Already committed",
      mps_overflow: "Committed beyond the cell",
      mps_excess: "Beyond the approved cell",
    },
    timeline: {
      need: "Needed",
      due: "Due",
      start: "Start",
      release: "Release",
      due_snap: "Moved back {{days}} days to the last working day",
      routing_time:
        "Routing time: {{days}} working days for {{ops}} operations, {{minutes}} min ({{centres}})",
      lead_time_days: "Lead time: {{days}} working days",
      source_snap: "Moved back {{days}} days to a working day of {{facility}}",
      release_buffer: "Release buffer: {{days}} working days",
      version_repicked: "BOM or routing picked again at the start date",
      clamped: "Moved to today",
      skippedDate: "{{date}} closed",
      exceptions: "{{count}} calendar exceptions closed dates on the way.",
      exceptions_one: "{{count}} calendar exception closed a date on the way.",
    },
    note: {
      exceeds_approved_mps: "Plans more than the approved MPS.",
      scrap_applied_on_children: "Scrap is applied on the components, not on this order.",
      position_below_zero: "The projected position was below zero.",
      commitment_output_in_on_hand:
        "Work already made for the MPS cell is counted with its work order, not again as stock.",
      firmed_quantity_kept: "Firmed, so the quantity is kept.",
      clamped_to_today: "A date in the past was moved to today.",
      firmed_dates_kept: "Firmed, so the dates are kept.",
      outsourced_step_not_timed: "An outsourced step is not timed.",
      source_uncalendared: "The source facility has no calendar.",
      work_center_closed_in_window: "A work centre is closed in this window.",
      work_center_uncalendared: "A work centre has no calendar; the facility's hours were used.",
    },
  },
  mrp: {
    title: "MRP",
    loading: "Loading the MRP runs…",
    loadError: "The MRP runs could not be loaded.",
    emptyTitle: "No MRP run yet",
    emptyBody: "Approve an MPS plan or run MRP.",
    historyTitle: "MRP run history",
    run: "Run MRP",
    running: "Running MRP…",
    runGroup: "Run group-wide",
    groupStarting: "Starting…",
    rerun: "Run again",
    runDone: "{{run}} finished at {{time}}.",
    runFailed: "{{run}} failed.",
    requestFailed: "MRP could not be started. Request ID: {{requestId}}",
    groupScopeRequired: "Running every facility needs MRP rights at group scope.",
    groupRunning: "A group-wide pass is already running ({{id}}). It plans this facility too.",
    groupRunningUnnamed: "A group-wide pass is already running. It plans this facility too.",
    groupStarted: "Group-wide pass started, in this order: {{order}}.",
    groupSkipped: "Skipped, with no calendar and nothing to plan: {{names}}.",
    failures:
      "Failed MRP runs in a row: {{count}}. The planned orders shown are from the last run that succeeded.",
    nightlySkipped:
      "No calendar and nothing to plan: the nightly run skipped this facility on {{date}}.",
    mpsExceeded: "Weeks where MRP plans more than the approved MPS: {{count}}.",
    proposeNow: "Propose now",
    openCalendars: "Open the calendars",
    graphExcluded: "Some products were left out for a BOM error. Open the run to see which.",
    fixpoint: {
      converged: "The last group-wide pass settled after pass {{pass}}.",
      not_reached:
        "The last group-wide pass did not settle after pass {{pass}}. Transfer orders may still move.",
    },
    status: {
      queued: "Queued",
      running: "Running",
      succeeded: "Succeeded",
      failed: "Failed",
    },
    trigger: {
      mps_approval: "MPS approved",
      manual: "By hand",
      transfer_projection: "Transfer demand",
      cron: "Nightly",
    },
    error: {
      FACILITY_CALENDAR_MISSING: "The facility has no calendar, so MRP cannot plan dates.",
      DEMAND_REFRESH_FAILED: "The demand refresh it needed failed.",
      LEASE_EXPIRED: "The run stopped answering and was given up.",
      SHUTDOWN: "The run was stopped by a restart.",
      PLANNED_ORDERS_CHANGED:
        "Planned orders changed while it ran, and it gave up after its retries.",
      TRANSFER_FIXPOINT_NOT_REACHED: "Transfer orders between facilities did not settle.",
      COMPUTE_FAILED: "The plan could not be computed.",
    },
    columns: {
      run: "Run",
      created: "Started",
      trigger: "Started by",
      status: "Status",
      duration: "Took",
      createdOrders: "Created",
      updatedOrders: "Updated",
      cancelledOrders: "Cancelled",
      messages: "Messages",
      error: "Error",
    },
    tiles: {
      mrp: "MRP",
      noRun: "No run yet",
      queued: "Queued, runs at {{time}}",
      orders: "Planned orders",
      summaryError: "The counts could not be read",
      byStatus: {
        proposed: "{{count}} proposed",
        firmed: "{{count}} firmed",
      },
      byKind: {
        manufacture: "{{count}} make",
        purchase: "{{count}} buy",
        transfer: "{{count}} transfer",
      },
      messages: "Action messages",
      noMessages: "None waiting",
    },
  },
  mrpRun: {
    title: "MRP run",
    loading: "Loading the run…",
    loadError: "The run could not be loaded.",
    notFound: "This run does not exist or is outside your scope.",
    back: "Back to the workbench",
    narrowed: "You see the counts of purchase orders only.",
    header: "Run",
    facility: "Facility",
    triggerCount: "Requests joined",
    created: "Queued",
    due: "Due",
    started: "Started",
    finished: "Finished",
    duration: "Took",
    plan: "MPS plan read",
    noPlan: "No approved plan; planned from the demand pool",
    planLink: "Open the MPS board",
    refresh: "Demand refresh read",
    refreshLink: "Open the demand pool",
    horizon: "Horizon",
    weeks: "{{count}} weeks",
    weeks_one: "{{count}} week",
    orchestration: "Group-wide pass",
    single: "Run on its own",
    pass: "Pass {{pass}}",
    depth: "Propagation depth",
    conflictRetries: "Conflict retries",
    levels: "Deepest BOM level",
    counts: "Counts",
    noCounts: "No counts yet. A queued or running run has none.",
    count: {
      productsPlanned: "Products planned",
      created: "Orders created",
      updated: "Orders updated",
      cancelled: "Orders cancelled",
      messages: "Action messages",
      optionalSkipped: "Optional BOM lines skipped",
      remnantMismatch: "Remnants the registry did not match",
      shutdownRequeues: "Requeued by a restart",
    },
    byKind:
      "{{kind}}: {{planned}} products, {{created}} created, {{updated}} updated, {{cancelled}} cancelled, {{messages}} messages",
    lists: "What the run reported",
    noLists: "Nothing reported yet.",
    none: "None",
    more: "{{count}} more not listed.",
    list: {
      skipped: "BOM lines left out, unit does not convert",
      skipped_work_orders: "Open work orders left out, quantity does not convert",
      no_bom: "Products with no BOM",
      supply_limited: "Products short of supply",
      defaulted_parameters: "Products planned on default parameters",
      transfer_source_inactive: "Transfers whose source is inactive",
      depth_exceeded: "Chains deeper than 12 levels",
      bom_cycle: "BOM cycles",
      byproduct_cycle: "By-product cycles",
      product_cycles: "Product cycles across facilities",
      uncalendared_work_centers: "Work centres with no calendar",
      closed_work_centers: "Work centres closed in the horizon",
      closed_facility_windows: "Facility windows closed",
      mps_exceeded: "Weeks over the approved MPS",
      mps_unplanned: "MPS products not planned",
    },
    skipReason: {
      UOM_NOT_CONVERTIBLE: "unit does not convert",
      OUTPUT_UOM_NOT_CONVERTIBLE: "output unit does not convert",
      MEASURE_PRECISION_LOSS: "quantity cannot be restated exactly",
    },
    leadSource: {
      product: "from the product",
      zero: "none set",
    },
    unplannedReason: {
      no_plan: "no approved plan",
      no_cell: "no cell in the plan",
    },
    entry: {
      skipped: "Line {{line}}: {{reason}}",
      skippedOrder: "{{order}}: {{reason}}",
      from: "from {{date}}",
      defaulted: "{{product}} ({{kind}}): {{days}} days lead time, {{source}}",
      transferInactive: "{{product}} from {{facility}}",
      window: "{{facility}}: {{from}} to {{to}}",
      week: "week of {{date}}",
      unplanned: "{{product}}: {{reason}}",
    },
    ordersOfRun: "Planned orders this run wrote",
  },
  // The finite schedule (spec 03 §4.1, §4.6 to §4.8, P3-I-2, D-406 ruling 8).
  schedule: {
    title: "Schedule",
    loading: "Loading the schedule…",
    loadError: "The schedule could not be loaded.",
    emptyTitle: "No schedule yet",
    emptyBody: "Firm planned orders or release work orders, then solve.",
    filteredTitle: "Nothing matches these filters",
    filteredBody: "No operation in these days matches {{filters}}.",
    firstDraft: "This facility's first schedule waits to be published.",
    version: "Version {{version}}",
    publishedAt: "Published {{time}}",
    publishedAuto: "Published automatically",
    publishedByPerson: "Published by a person",
    draftOnly: "Not published yet",
    unplacedMore: "{{count}} more unplaced operations are not listed.",
    unplacedMore_one: "{{count}} more unplaced operation is not listed.",
    zoneNote: "Times are in the facility's zone, {{zone}} ({{offset}}).",
    lastSolve: "Last solve {{time}}",
    pending: "Solve queued by {{count}} changes, runs at {{time}}.",
    pending_one: "Solve queued by {{count}} change, runs at {{time}}.",
    pendingUrgent: "An urgent change is waiting. It solves now.",
    failures: "Failed solves in a row: {{count}}. The board shows the last schedule published.",
    daysLabel: "Days shown",
    days: "{{count}} days",
    zoomLabel: "Zoom",
    zoomWeek: "Week",
    zoomDay: "Day",
    earlier: "Earlier",
    later: "Later",
    range: "{{from}} to {{to}}",
    times: "{{start}} to {{end}}",
    kpis: {
      lateOrders: "Late orders",
      lateDays: "Late days",
      setupMinutes: "Setup minutes",
      overloads: "Overloads",
      unplaced: "Not placed",
    },
    filters: {
      order: "Order number",
      product: "Product",
      key: "Setup key",
      workCenter: "Work centre",
    },
    lane: {
      tooltip:
        "One line per work centre: parallel machines are scheduled as one line until equipment records exist",
      noCapacity: "No working time in these days",
      utilization: "Utilization {{pct}} %",
      availabilityUnbound: "Availability 100 %. Derating needs maintenance records",
      availability: "Availability {{pct}} %",
      blocked: "Blocked by maintenance",
      unplacedTitle: "Not placed: {{count}}",
    },
    block: {
      aria: "{{order}}, {{step}}, {{start}} to {{end}}",
      workOrder: "Work order",
      plannedOrder: "Planned order",
      inProgress: "In progress",
      pinned: "Pinned",
      fenced: "Kept in its place by the frozen period",
      fencedShort: "Frozen",
      late: "Late",
      setup: "{{minutes}} min changeover",
      setupShort: "{{minutes}}′",
    },
    unplaced: {
      work_center_uncalendared: "No calendar assigned. Zero capacity.",
      work_center_blocked: "Blocked by maintenance.",
      no_capacity: "No working time left in the search window.",
      on_hold: "On quality hold",
      predecessor_unplaced: "The step before it is not placed.",
      no_routing: "The order has no routing.",
    },
    runStatus: {
      running: "Solving",
      succeeded: "Done",
      failed: "Failed",
    },
    trigger: {
      cron: "Nightly",
      event: "Change",
      manual: "Person",
      drag_preview: "Preview",
      commit_move: "Move",
      apply_move: "Applied move",
      ctp_probe: "Promise check",
      scenario: "What-if",
    },
    runError: {
      LEASE_EXPIRED: "The solve stopped answering and was given up.",
      SHUTDOWN: "The system restarted during the calculation. It runs again.",
      TOO_MANY_OPERATIONS:
        "More than 5,000 operations wait at this facility, too many to solve at once.",
      COMPUTE_FAILED: "The solver could not compute a schedule.",
    },
    solve: {
      button: "Solve",
      running: "Solving…",
      queued: "A solve is already running. Yours runs right after it.",
      requestFailed: "The solve could not be started. Request ID: {{requestId}}",
      outcome: {
        wrote: "Solved. The new plan waits for a person to publish it.",
        short_circuit: "Nothing changed since the last solve, so no new version was written.",
        stale_base: "A schedule was published while this one solved. It solves again now.",
        empty: "There is nothing to schedule at this facility.",
        auto_published: "Solved and published automatically.",
        failed: "The solve failed.",
        running: "Solving…",
      },
    },
    overload: {
      aria: "Overload of {{hours}} hours in {{week}}",
      marker: "{{hours}} h over",
      title: "Overload on {{wc}} in {{week}}",
      body: "{{hours}} hours of work due by {{date}} cannot be done by then: {{required}} h required against {{capacity}} h of capacity.",
      lateOps: "Late or unplaced operations: {{count}}",
      noLateOps: "No operation of this week is late.",
      due: "due {{date}}",
      unplaced: "not placed",
      more: "{{count}} more are not listed.",
    },
    moves: {
      title: "Ways to clear it",
      scoring: "Scoring…",
      scoringNote: "The moves are being scored against the published plan.",
      abandoned: "Not scored: a newer schedule was published.",
      none: "No move could be proposed for this overload.",
      rank: "Option {{rank}}",
      kind: {
        overtime: "Overtime",
        alternate_work_center: "Alternate work centre",
        push_order: "Push an order",
      },
      overtime: "Add {{hours}} h of extra shift on {{wc}}",
      alternate: "Move {{count}} operations, {{hours}} h of work, from {{from}} to {{to}}",
      alternate_one: "Move {{count}} operation, {{hours}} h of work, from {{from}} to {{to}}",
      push: "Run {{order}} after every other order on {{wc}}, freeing {{hours}} h",
      interval: "{{from}} to {{to}}",
      op: "{{order}} · step {{no}}, {{minutes}} min",
      effectLate: "Late orders: {{before}} now, {{after}} after",
      effectLatePending: "Late orders: {{before}} now. Scoring…",
      effectLateUnscored:
        "Late orders: {{before}} now. Not scored: the scoring was spent on larger overloads first.",
      effectLateAbandoned:
        "Late orders: {{before}} now. Not scored: a newer schedule was published.",
      clears: "Clears the overload",
      notClears: "Does not clear the overload",
      cost: "Cost {{cost}}",
      atRisk: "Order value at risk {{value}}",
      costHidden: "Cost hidden",
      unpriced: {
        currency_mismatch: "Not priced: the rates are in another currency",
        no_rate: "Not priced: the work centre has no labour rate",
        no_standard_cost: "Not priced: no standard cost",
      },
      atRiskUnpriced: {
        currency_mismatch: "Order value at risk not priced: the costs are in another currency",
        no_rate: "Order value at risk not priced",
        no_standard_cost: "Order value at risk not priced: no standard cost",
      },
      apply: "Apply",
      applying: "Applying…",
      overtimeAdmin: "A facility admin applies overtime",
      readOnly: "A planner or a facility admin applies moves.",
      confirmTitle: "Apply this move?",
      confirmOvertime:
        "This adds these extra shifts to the calendar of {{wc}}, then solves and publishes a new version:",
      confirmAlternate:
        "This keeps these operations on {{to}}, then solves and publishes a new version:",
      confirmPush:
        "This defers {{order}} behind every other order until you remove the defer under Pins. The plan is then re-solved and published.",
      back: "Back to the moves",
      applied: "Applied: {{move}}, by {{who}}, {{time}}.",
      alreadyApplied: "This overload's move was already applied. The board was read again.",
      notFound: "This move is no longer offered. The moves were read again.",
      denied: "Applying this move needs {{codes}}.",
      code: {
        calendar: "permission to change calendars",
        cost: "permission to read costs",
      },
      notApplicable: {
        order_closed: "The order has closed since this move was proposed.",
        work_center_archived: "The work centre has been archived since this move was proposed.",
        operation_done: "The operation has finished since this move was proposed.",
      },
      failed: "The move could not be applied. Request ID: {{requestId}}",
    },
    move: {
      promised: "Promised to customer",
      promisedNo: "Promised to customer: {{number}}",
      title: "What this move changes",
      heading: "{{order}} · {{step}} to {{time}} on {{wc}}",
      previewing: "Working out what this move changes…",
      formTitle: "Move this operation",
      start: "New start",
      lane: "Work centre",
      earlier: "1 h earlier",
      later: "1 h later",
      preview: "Preview the move",
      invalidStart: "Enter a date and a time.",
      sameSpot: "Choose another start or work centre first.",
      blocked: {
        in_progress: "A started operation cannot be moved.",
        outsourced: "An outsourced step holds no line, so it cannot be moved.",
        on_hold: "A step on quality hold cannot be moved until the hold is released.",
      },
      pinned: "Pinned at this start. Remove the pin under Pins to let the solve move it.",
      pinReleased: "The last solve released this operation's pin.",
      phone: "Operations are moved on a larger screen.",
      dragHint:
        "Drag a block along its line or onto another allowed line to see what the move changes. Every block's drawer also has a move form.",
      laneNotAllowed: "This step cannot run on {{wc}}.",
      dragTo: "{{time}} on {{wc}}",
      newlyLate: "Orders that newly finish late: {{count}}",
      resolved: "Orders no longer late: {{count}}",
      noneLate: "No order newly finishes late.",
      noneResolved: "None",
      slip: "{{order}}, due {{due}}, ends {{end}} instead of {{old}}: {{count}} days late",
      slip_one: "{{order}}, due {{due}}, ends {{end}} instead of {{old}}: {{count}} day late",
      orderFor: "{{order}} for {{customer}}",
      gain: "{{order}}, due {{due}}, now ends {{end}}",
      unplacedEnd: "not placed",
      moved: "Operations it moves: {{count}}",
      more: "{{count}} more are not listed.",
      kpis: "Change to the plan",
      newOverloads: "New overloads: {{count}}",
      precedence:
        "This start is before the step ahead of it ends ({{time}}), which the order cannot do. Choose a later start.",
      confirm: "Confirm",
      confirming: "Saving…",
      cancel: "Cancel",
      stale: "The plan changed while you looked. Here is the new result.",
      previewAgain: "Preview again",
      refused: {
        OP_IN_PROGRESS: "A started operation cannot be moved.",
        OP_NOT_MOVABLE: "A step on quality hold or an outsourced step cannot be moved.",
        MOVE_IN_PAST: "That start is in the past.",
        WORK_CENTER_NOT_ALLOWED: "This step cannot run on that work centre.",
        PIN_OVERLAPS_FIXED_WORK:
          "That place overlaps work that cannot move: a started step or another pin.",
        SCHEDULE_OPERATION_NOT_FOUND: "This operation is no longer on the schedule.",
        MOVE_BREAKS_PRECEDENCE:
          "The move would start a step before the step ahead of it ends. Choose a later start.",
        SCHEDULE_RUN_NOT_FOUND: "The preview is no longer available. Preview again.",
      },
      failed: "The move could not be worked out or saved. Request ID: {{requestId}}",
      ghost: "New place of {{order}}, {{start}} to {{end}}",
      outcome: {
        moved: "Moved and pinned. Version {{version}} is published.",
        applied: "Move applied. Version {{version}} is published.",
        superseded: "A newer schedule was published. Reloaded.",
      },
    },
    kpiDelta: {
      late_orders: "Late orders",
      total_late_days: "Late days",
      total_setup_minutes: "Setup minutes",
      overloads: "Overloads",
      unplaced: "Not placed",
      makespan_hours: "Hours to finish all work",
    },
    pins: {
      button: "Pins ({{count}})",
      buttonLoading: "Pins",
      title: "Pins and deferred orders",
      loading: "Loading the pins…",
      loadError: "The pins could not be loaded.",
      emptyTitle: "Nothing is pinned",
      emptyBody: "Drag a block on the board to pin it in place, or apply a move to an overload.",
      kind: {
        pin_position: "Pinned in place",
        pin_work_center: "Kept on a work centre",
        defer: "Deferred",
      },
      origin: {
        commit_move: "set by a move on the board",
        apply_move: "set by an applied move",
      },
      at: "from {{time}}",
      on: "on {{wc}}",
      step: "step {{no}} · {{name}}",
      since: "Since {{time}}, {{origin}}",
      unpin: "Unpin",
      undefer: "Remove the defer",
      removing: "Removing…",
      removed: "Removed. The next solve places this work freely.",
      notActive: "This pin had already ended. The list was read again.",
      failed: "The pin could not be removed. Request ID: {{requestId}}",
      readOnly: "A planner or a facility admin removes pins.",
    },
    drawer: {
      label: "Operation of {{order}}",
      product: "Product",
      qty: "Quantity",
      salesOrder: "Sales order",
      step: "Step",
      stepText: "{{no}} · {{name}}",
      workCenter: "Work centre",
      start: "Start",
      end: "End",
      sequence: "Position on the line",
      notPlaced: "Not placed",
      run: "Work",
      runText: "{{run}} min of run and {{setup}} min of changeover, {{wall}} min on the clock",
      runOnly: "{{run}} min of run",
      due: "Due",
      noDue: "No due date",
      lateBy: "{{count}} days late",
      lateBy_one: "{{count}} day late",
      dueSameDay: "ends on its due date",
      spare: "{{count}} days to spare",
      spare_one: "{{count}} day to spare",
      priority: "Priority",
      batch: "Batch",
      noBatch: "Not part of a batch",
      batchKeyOnly: "Runs as a batch on {{key}}",
      batchWith: "Runs with {{orders}} on {{key}} with no changeover between them",
      changeover: "Changeover",
      noChangeover: "No changeover",
      changeoverMinutes: "{{minutes}} min",
      key: "Setup key {{key}}",
      previousUnknown: "The work before it on this line is outside the days shown.",
      keyMissing: "A setup key is missing, so the routing step's own setup minutes apply.",
      explaining: "Pricing the changeover…",
      explainError: "How the changeover was priced could not be loaded.",
    },
    draft: {
      title: "New plan waiting",
      badge: "New plan waiting (version {{version}})",
      waiting: "New plan waiting",
      review: "Review and publish",
      notDraft: "The plan changed while you read it. Here is the newer one.",
      failed: "The plan could not be decided. Request ID: {{requestId}}",
      heldBy: "It waits for a person because:",
      loading: "Loading what the plan changes…",
      loadError: "What the plan changes could not be loaded.",
      first: "There is no published schedule to compare it with.",
      moved: "Operations it moves: {{count}}",
      movedNone: "It moves no operation.",
      movedText: "from {{from}} to {{to}}",
      laneChanged: "from {{from}} to {{to}}",
      more: "{{count}} more are not listed.",
      newOverloads: "New overloads: {{count}}",
      clearedOverloads: "Cleared overloads: {{count}}",
      none: "None",
      discard: "Discard",
      discarding: "Discarding…",
      publish: "Publish",
      publishing: "Publishing…",
      readOnly: "A planner or a facility admin publishes a plan.",
      outcome: {
        published: "Version {{version}} is published. The floor follows it now.",
        discarded: "The plan is discarded. The published schedule stays in force.",
        superseded: "A newer schedule was published. Reloaded.",
        decided: "This plan was already decided. The board shows the schedule in force.",
      },
    },
    held: {
      first_schedule: "It is this facility's first schedule.",
      auto_publish_off: "Automatic publishing is off at this facility.",
      ops_moved: "It moves operations further than their tolerance.",
      new_overloads: "It has new overloads.",
      pins_released: "It released a pinned operation.",
    },
    heldShort: {
      first_schedule: "first schedule",
      auto_publish_off: "automatic publishing off",
      ops_moved: "operations moved",
      new_overloads: "new overloads",
      pins_released: "pin released",
    },
    phone: {
      lane: "Work centre",
      prevDay: "Previous day",
      nextDay: "Next day",
      none: "Nothing is scheduled on this line this day.",
      overloads: "Overloads",
    },
    workbench: {
      title: "Schedule",
      schedule: "Published schedule",
      noPublished: "Nothing published yet",
      lateOrders: "Late orders: {{count}}",
      overloads: "Overloads: {{count}}",
      lastSolve: "Last solve",
      noSolve: "No solve yet",
      queued: "Queued, runs at {{time}}",
      overloadTile: "Open overloads",
      noOverloads: "None",
    },
  },
  capacity: {
    title: "Capacity",
    loading: "Loading capacity…",
    loadError: "Capacity could not be loaded.",
    draftError: "The waiting plan's capacity could not be loaded.",
    noCalendarsTitle: "No calendars configured",
    noCalendarsBody: "Assign shift patterns in Calendars.",
    openCalendars: "Open Calendars",
    emptyTitle: "No schedule yet",
    emptyBody: "Firm planned orders or release work orders, then solve.",
    noRows: "The published schedule holds no work centre with capacity or load.",
    noCapacity:
      "No work centre here has working time in these days. Assign shift patterns in Calendars.",
    grainLabel: "Show by",
    grain: {
      week: "Week",
      day: "Day",
    },
    range: "Version {{version}}, {{from}} to {{to}}",
    showDraft: "Compare the waiting plan",
    figures: "{{placed}} h placed of {{capacity}} h · {{due}} h due",
    proposed: "Proposed, not firmed: {{hours}} h",
    overload: "{{hours}} h over",
    delta: "Waiting plan: {{hours}} h",
  },
  matrix: {
    title: "Setup matrix",
    loading: "Loading the changeover times…",
    loadError: "The changeover times could not be loaded.",
    noWcTitle: "No work centre here",
    noWcBody: "Add work centres to this facility before setting changeover times.",
    readOnly: "You can read these changeover times. A facility admin changes them.",
    phoneReadOnly: "Changeover times are edited on a larger screen.",
    workCenter: "Work centre",
    saveFirst: "Save or discard your changes before choosing another work centre.",
    unused: "Not used by any routing on this line",
    from: "From",
    to: "To",
    minutes: "Minutes",
    lint: "Check",
    any: "any value",
    absent: "facet not in the key",
    opaqueFacet: "Free-form keys",
    facet: "Facet {{facet}}",
    emptyTitle: "No changeover times",
    emptyBody: "The scheduler uses each routing step's setup minutes.",
    addRow: "Add row",
    removeRow: "Remove this row",
    remove: "Remove",
    newFacet: "New facet",
    addFacet: "Add facet",
    facetInvalid:
      "A facet is lower-case letters, digits and _, starting with a letter, at most 32 characters.",
    discard: "Discard changes",
    fixRows: "{{count}} rows need a correction before they can be saved.",
    fixRows_one: "{{count}} row needs a correction before it can be saved.",
    tooMany: "A work centre holds at most {{max}} rows.",
    saved: "Saved. The line has {{count}} changeover rows.",
    saved_one: "Saved. The line has {{count}} changeover row.",
    reason: {
      facet_malformed: "The facet does not follow the key's rules.",
      value_malformed: "A value is not a key value, *, or -.",
      same_value: "From and to are the same value, which is no change.",
      duplicate_row: "This change is already listed.",
      minutes_out_of_range: "Minutes are a whole number from 0 to 1440.",
    },
    basis: {
      missing_key: "One of the keys is missing, so the routing step's own setup minutes apply.",
      same_key: "The keys are the same, so there is no changeover.",
      matrix: "Every facet that changes has a row, so the changeover is their sum.",
      matrix_incomplete:
        "A facet that changes has no row, so the changeover is the larger of the rows' sum and the routing step's setup minutes.",
    },
    test: {
      title: "Test a changeover",
      body: "Enter the key the line ran last and the key it runs next to see what the scheduler charges.",
      savedOnly: "This checks the saved rows. Save first to check your changes.",
      from: "From key",
      to: "To key",
      static: "Routing setup minutes",
      run: "Check",
      running: "Checking…",
      error: "The changeover could not be checked.",
      result: "Changeover: {{minutes}} min",
      facetRow:
        "{{facet}}: {{from}} → {{to}}, priced by the row {{rowFrom}} → {{rowTo}} at {{minutes}} min",
      facetNoRow: "{{facet}}: {{from}} → {{to}}, no row",
    },
  },
  // §4.9 Promises and the shared CTP dialog (P3-J-1, D-474).
  promises: {
    tile: {
      title: "Promises",
      atRisk: "At risk",
      broken: "Broken in the last 30 days",
      upcoming: "Due in the next 7 days",
      upcomingNone: "Nothing is promised for the next 7 days.",
      empty: "No promises at risk.",
      atLeast: "{{count}}+",
      next: "{{number}} · {{date}}",
    },
    title: "Promises",
    checkDate: "Check a date",
    loading: "Loading promises…",
    loadError: "The promises could not be loaded.",
    emptyTitle: "No promises yet",
    emptyBody: "Check a date from a quote or an order.",
    filteredEmptyTitle: "No promises match",
    filteredEmptyBody: "Nothing matches {{filters}}.",
    salesOrder: "Sales order",
    customer: "Customer",
    columns: {
      promiseNo: "Promise",
      product: "Product",
      qty: "Quantity",
      customer: "Customer",
      order: "Order",
      requested: "Asked for",
      promised: "Promised",
      projected: "Projected",
      path: "From",
      status: "Status",
      risk: "Risk",
      source: "Source",
    },
    filters: {
      status: "Status",
      live: "Live",
      from: "Promised from",
      to: "Promised to",
      product: "Product",
      customer: "Customer",
      order: "Order",
      anyOrder: "Any order of this customer",
      orderNeedsCustomer: "Choose a customer first",
    },
    filterTag: {
      status: "Status: {{status}}",
      from: "From {{date}}",
      to: "To {{date}}",
      product: "Product: {{product}}",
      customer: "One customer",
      order: "One order",
      customerNamed: "Customer: {{customer}}",
      orderNamed: "Order: {{order}}",
    },
    customerPicker: {
      placeholder: "Name or customer number",
      results: "Matching customers",
      searching: "Searching…",
      error: "The customers could not be searched.",
      none: "No customer matches.",
    },
    status: {
      quoted: "Quoted",
      committed: "Committed",
      at_risk: "At risk",
      broken: "Broken",
      kept: "Kept",
      withdrawn: "Withdrawn",
      expired: "Expired",
    },
    path: {
      offcut: "Off-cuts",
      stock: "Stock",
      production: "Production",
      transfer: "Transfer",
    },
    source: {
      dialog: "Checked by a person",
      agent: "Agent",
      confirmation: "Order confirmation",
      re_promise: "New promise",
    },
    risk: {
      stock_consumed: "Stock used elsewhere",
      material_delay: "Material late",
      capacity_loss: "Capacity lost",
      schedule_slip: "Schedule slipped",
    },
    withdrawnReason: {
      manual: "Withdrawn by a person",
      re_promised: "Replaced by a new promise",
      order_cancelled: "Order cancelled",
      line_cancelled: "Line cancelled",
    },
    basis: {
      title: "What it rests on",
      none: "Nothing to check.",
      offcut: "Off-cut, {{length}} m",
      stock: "From stock: {{qty}}",
      inTransit: "In transit: {{qty}}",
      workOrder: "A work order in the plan",
      plannedOrder: "A planned order",
      window: "{{workCenter}}, {{from}} to {{to}}",
      workCenter: "Work centre",
      material: {
        on_hand: "{{product}} in stock",
        planned_order: "{{product}} arriving {{date}}",
        lead_time: "{{product}} bought in by {{date}}",
      },
      holds: "Holds",
      broken: "No longer holds",
      unchecked: "Not checked yet",
      margin: "Safety margin: {{count}} d",
      beyondHorizon: "Estimated past the schedule's {{days}} days",
      beyondHorizonShort: "Estimated past the end of the schedule",
    },
    verdict: {
      title: "Verdict",
      kept: "Shipped on time",
      broken: "Shipped late or short",
      onTime: "On time: {{qty}} of {{judged}}",
      shortfall: "Short: {{qty}}",
      slip: "Late by {{count}} d",
      correctedAt: "Corrected after a late shipment, {{at}}",
      shipped: "Shipped {{date}}",
      downgraded: "Mostly on time, so it counts as a minor break.",
      corrected: "Corrected after a late shipment",
    },
    drawer: {
      label: "Promise",
      loading: "Loading the promise…",
      loadError: "The promise could not be loaded.",
      gone: "This promise no longer exists.",
      dates: "Dates",
      earliest: "Earliest",
      validUntil: "Quote holds until",
      committed: "Committed",
      timeline: "History",
      withdraw: "Withdraw",
      withdrawTitle: "Withdraw this promise?",
      withdrawReason: "Reason",
      withdrawConfirm: "Withdraw",
      withdrawing: "Withdrawing…",
    },
    dialog: {
      title: "Check a date",
      product: "Product",
      changeProduct: "Change",
      qty: "Quantity",
      qtyInvalid: "Enter a quantity above 0, at most three decimals.",
      requested: "Date asked for",
      facility: "Facility",
      offcuts: "Use off-cuts",
      line: "Order line",
      check: "Check date",
      checking: "Checking…",
      proposed: "Proposed date",
      earliest: "Earliest possible: {{date}}",
      byRequested: "By the date asked",
      afterRequested: "Later than asked",
      offcutOption: "Off-cut option",
      offcutUnit: "{{length}} m",
      waste: "Left over: {{qty}} m",
      validUntil: "This quote holds until {{at}}.",
      use: "Use in quote",
      commit: "Commit to the order line",
      committing: "Committing…",
      committed: "Committed. {{number}} is on the order line.",
      noDate: "No date could be found.",
      covered: "Stock covers {{covered}}, short by {{shortfall}}.",
      stale: "The plan moved since this quote.",
      newDate: "New date: {{date}}",
      rePromise: "Re-promise",
      rePromised: "Re-promised. The line now reads {{date}}.",
      rePromisedTold: "Re-promised. The line now reads {{date}}, and the customer will be told.",
      oldDate: "Date now: {{date}}",
      oldDateNone: "The line has no date yet.",
      notifyCustomer: "Tell the customer",
      acceptNew: "Accept new date",
      noCode: "Your role cannot check dates. Ask a planner.",
    },
    staleReason: {
      date_moved: "The date moved.",
      unit_unavailable: "An off-cut is no longer free.",
      unplaced: "The work no longer fits the schedule.",
    },
    reason: {
      no_routing: "The product has no routing.",
      no_bom: "The product has no bill of materials.",
      facility_calendar_missing: "A work centre has no calendar.",
      too_many_operations: "Too many steps to check at once.",
      probe_unstable: "The check could not settle. Try again.",
    },
    lineReason: {
      order_not_confirmed: "The order is not confirmed.",
      credit_blocked: "The order is held by its credit check.",
      line_cancelled: "The line is cancelled.",
      line_shipped: "The line has shipped.",
      customer_mismatch: "The line is for another customer.",
      product_mismatch: "The line is for another product.",
      facility_mismatch: "The line ships from another facility.",
      line_mismatch: "The promise was checked for another line.",
      qty_short: "The line asks for more than this quote.",
      promise_kept: "The line's promise was already kept, so it takes no new date.",
    },
    refusal: {
      FACILITY_NOT_FOUND: "That facility no longer exists.",
      PRODUCT_NOT_FOUND: "That product no longer exists.",
      ORDER_LINE_NOT_FOUND: "That order line no longer exists.",
      PRODUCT_ARCHIVED: "That product is archived.",
      CAPACITY_PATH_UNAVAILABLE: "No date could be found.",
      CTP_BUSY: "The planner is busy. Try again in a moment.",
      PROMISE_NOT_FOUND: "This promise no longer exists.",
      PROMISE_NOT_QUOTED: "This promise is no longer a quote.",
      PROMISE_EXPIRED: "This quote expired. Check the date again.",
      ORDER_LINE_REQUIRED: "Choose an order line to commit to.",
      ORDER_LINE_NOT_PROMISABLE: "This order line cannot take a promise.",
      LINE_ALREADY_PROMISED: "This order line already has a promise.",
      PROMISE_NOT_WITHDRAWABLE: "This promise can no longer be withdrawn.",
      ORDER_NOT_FOUND: "That order no longer exists.",
      PERMISSION_DENIED: "Your role cannot change this date. Ask a planner.",
      other: "That did not work. Request ID: {{requestId}}",
    },
  },
  // Where a work order or a transfer came from (D-396 ruling 23).
  scenarios: {
    title: "What-if",
    newScenario: "New scenario",
    loading: "Loading scenarios…",
    loadError: "The scenarios could not be loaded.",
    emptyTitle: "No scenarios yet",
    emptyBody: "Try 'What if the twister is down 5 days?'",
    filteredEmptyTitle: "No scenarios match",
    filteredEmptyBody: "No scenario has the status {{status}}.",
    filters: {
      status: "Status",
      any: "Any status",
    },
    filterTag: {
      status: "Status: {{status}}",
    },
    columns: {
      name: "Scenario",
      status: "Status",
      base: "Based on",
      created: "Created",
      lateDelta: "Late orders",
    },
    baseVersion: "Version {{version}}",
    created: "{{who}}, {{time}}",
    agent: "Agent",
    notSolved: "Not solved",
    status: {
      open: "Solving",
      solved: "Solved",
      failed: "Failed",
      promoted: "Promoted",
      discarded: "Discarded",
    },
    error: {
      NO_PUBLISHED_SCHEDULE: "The plant has no published schedule to compare with.",
      TOO_MANY_OPERATIONS: "The scenario holds more than 5,000 operations.",
      COMPUTE_FAILED: "The solve failed.",
      LEASE_EXPIRED: "The solve took too long and was stopped.",
      SHUTDOWN: "The solve was interrupted.",
    },
    kind: {
      add_shift: "Add a shift",
      machine_down: "Machine down",
      add_order: "Add an order",
      change_efficiency: "Change efficiency",
      drop_order: "Drop an order line",
    },
    codeName: {
      scenarioPromote: "promote scenarios",
      calendarManage: "manage calendars",
      costRead: "read costs",
    },
    problem: {
      SCENARIO_LIMIT_REACHED:
        "The facility already has {{limit}} open scenarios. Discard one first.",
      NO_PUBLISHED_SCHEDULE: "Publish a schedule first; a scenario is compared with it.",
      WORK_CENTER_NOT_FOUND: "Change {{n}}: the work centre was not found.",
      PRODUCT_NOT_FOUND: "Change {{n}}: the product was not found.",
      ORDER_LINE_NOT_FOUND: "Change {{n}}: the order line was not found.",
      SCENARIO_NOT_FOUND: "The scenario no longer exists.",
      SCENARIO_NOT_SOLVABLE: "This scenario cannot be solved again now.",
      SCENARIO_NOT_SOLVED: "Only a solved scenario can be promoted.",
      SCENARIO_CLOSED: "This scenario is already closed.",
      NOT_PERSISTABLE: "Nothing in this scenario can be saved.",
      PERMISSION_DENIED: "Promoting also needs the right to {{codes}}.",
      FACILITY_NOT_FOUND: "The facility was not found.",
      generic: "The request failed: {{title}}",
    },
    builder: {
      title: "New scenario",
      name: "Name",
      namePlaceholder: "Twister down 5 days",
      changes: "What changes",
      addKind: "Kind of change",
      add: "Add a change",
      remove: "Remove",
      removeAria: "Remove change {{n}}",
      row: "Change {{n}}",
      limit: "At most {{count}} changes in one scenario.",
      empty: "Add at least one change.",
      zoneNote: "Times are in the facility's zone ({{offset}}).",
      submit: "Create and solve",
      submitting: "Creating…",
      cancel: "Cancel",
      workCenter: "Work centre",
      chooseWorkCenter: "Choose a work centre",
      noWorkCenters: "This facility has no active work centres.",
      starts: "Starts",
      ends: "Ends",
      capacityFactor: "Capacity factor",
      product: "Product",
      productChosen: "Product: {{product}}",
      changeProduct: "Change product",
      qty: "Quantity",
      dueDate: "Due date",
      priority: "Priority",
      priorityRush: "{{n}}, rush",
      priorityNormal: "{{n}}, normal",
      priorityLow: "{{n}}, low",
      shift: "Shift",
      chooseShift: "Choose a shift",
      efficiency: "Efficiency, %",
      order: "Order",
      chooseOrder: "Choose an order",
      noOrders: "This facility has no confirmed orders.",
      line: "Line",
      chooseLine: "Choose a line",
      lineOption: "Line {{line}}: {{product}}",
      ordersError: "The orders could not be loaded.",
      fixErrors: "Check the marked fields.",
      phone: "Build scenarios on a larger screen.",
      issue: {
        required: "Fill this in.",
        window: "The end must come after the start, within 92 days.",
        factor: "A factor from 0.01 to 3.00.",
        qty: "A quantity above 0, at most three decimals.",
        efficiency: "A whole number from 10 to 200.",
        date: "Enter a date.",
        name: "Give the scenario a name of at most 120 characters.",
      },
    },
    result: {
      back: "All scenarios",
      loading: "Loading the scenario…",
      loadError: "The scenario could not be loaded.",
      notFoundTitle: "Scenario not found",
      notFoundBody: "It may have been discarded and removed.",
      solving: "Solving on a copy of the plan… {{elapsed}}",
      solvingBody: "Nothing live changes until someone promotes the scenario.",
      failedTitle: "The solve failed",
      solveAgain: "Solve again",
      superseded: "Based on version {{version}}; the plant has moved on.",
      base: "Compared with version {{version}} of the published schedule.",
      changes: "What changes",
      hypothesis: {
        add_shift: "Extra shift on {{wc}}, {{start}} to {{end}}, capacity × {{factor}}",
        machine_down: "{{wc}} down from {{start}} to {{end}}",
        add_order: "New order: {{qty}} of {{product}}, due {{date}}, priority {{priority}}",
        change_efficiency: "{{wc}}, shift {{shift}} at {{pct}} %",
        drop_order: "One order line taken out of the plan",
      },
      saved: "Saved on promote",
      notSaved: "Not saved: this answers a question only",
      kpis: "Results",
      kpi: {
        lateOrders: "Late orders",
        lateDays: "Late days",
        setup: "Setup minutes",
        makespan: "Hours to finish all work",
      },
      kpiSub: "{{base}} → {{scenario}}",
      utilization: "Utilization by work centre",
      util: {
        workCenter: "Work centre",
        base: "Published",
        scenario: "Scenario",
        delta: "Change",
      },
      pct: "{{pct}} %",
      noUtil: "No work centre's load changes.",
      cost: "Cost impact",
      overtime: "Overtime",
      costUnpriced: {
        no_rate: "Not priced: a work centre has no labour rate.",
        currency_mismatch: "Not priced: the labour rates are in another currency.",
      },
      latePenalty: "Late penalties are not priced.",
      slipped: "Orders that finish later ({{count}})",
      recovered: "Orders that finish sooner ({{count}})",
      slippedNone: "No order finishes later.",
      recoveredNone: "No order finishes sooner.",
      more: "{{count}} more not listed.",
      shift: {
        order: "Order",
        customer: "Customer",
        base: "Published end",
        scenario: "Scenario end",
        days: "Days",
      },
      unplaced: "Not placed",
      whatIfOrder: "What-if order {{n}}",
      lanes: "Sandbox schedule",
      lanesNone: "Nothing is scheduled in this window.",
      lanesError: "The sandbox schedule could not be loaded.",
      lanesLoading: "Loading the sandbox schedule…",
      earlier: "Earlier",
      later: "Later",
      window: "{{from}} to {{to}}",
      zoneLine: "Times are in the facility's zone ({{offset}}).",
      lane: "Work centre",
      promote: "Promote",
      promoteTip: "Only added shifts and efficiency changes can be saved.",
      discard: "Discard",
      duplicate: "Duplicate",
      copyName: "{{name}} (copy)",
      promoted: "Promoted by {{who}}, {{time}}",
      discarded: "Discarded {{time}}",
      applied: "Written to the calendar: {{shifts}} extra shifts, {{patterns}} pattern copies.",
      phoneNote: "Promote, discard and duplicate on a larger screen.",
      confirm: {
        title: "Promote this scenario?",
        body: "This writes to the live calendar and asks for a new live schedule.",
        writes: "Will be written",
        skips: "Will not be written",
        shiftLine: "Extra shift on {{wc}}, {{start}} to {{end}}",
        patternLine:
          "A copy of {{wc}}'s shift pattern with shift {{shift}} at {{pct}} %, from the next working day",
        submit: "Promote",
        submitting: "Promoting…",
      },
      discardConfirm: {
        title: "Discard this scenario?",
        body: "The scenario and its sandbox schedule are removed after 30 days.",
        submit: "Discard",
      },
      done: {
        solve: "Solving again.",
        discard: "Scenario discarded.",
        promote: "Promoted. A new live schedule is being solved.",
        duplicate: "Copy created and solving.",
      },
    },
  },
  origin: {
    fromPlannedOrder: "From a planned order",
    fromPlannedOrderNo: "From {{number}}",
  },
  grid: {
    label: "Quantities by week",
    product: "Product",
    cell: "{{product}}, {{week}}",
    quantity: "Quantity",
    reason: "Reason for the change",
    commit: "Apply",
    showDetails: "Show details for {{product}}",
    hideDetails: "Hide details for {{product}}",
    frozen: "Frozen, it cannot change",
    overridden: "Changed by a person",
    dropped: "A change was dropped",
    edited: "Not saved yet",
    busy: "Saving",
    previousWeek: "Previous week",
    nextWeek: "Next week",
    phoneWeek: "Week {{week}}",
    invalid: {
      not_number: "Enter a number, 0 or more.",
      precision: "At most three decimals.",
    },
  },
} as const;

export type PlanningDict = Translated<typeof planningEn>;

export const planningRu: PlanningDict = planningRuDict;
